import fuzzysort from 'fuzzysort';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Dialog} from '../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogHeader} from '../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {DropdownList} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {SimpleDropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownItem';
import {SideFrameDialogDefinition} from './sideFrameDialogsHooks';

/*
 * Props.
 */

interface SideFrameFuzzyListProps {
  fuzzyListData: FuzzyListDefinition;
  onItemSelect: (arg: ListItem | null) => void;
}

/*
 * Style.
 */

const StyledTextBoxDiv = styled.div`
  width: 300px;
  margin: 0 10px;
`;

const StyledScrollDiv = styled.div`
  height: 300px;
  overflow-y: auto;
  padding: 20px;
`;

/*
 * Component.
 */

const SideFrameFuzzyList: FC<SideFrameFuzzyListProps> = ({fuzzyListData, onItemSelect}) => {
  const {items} = fuzzyListData;
  const [search, setSearch] = useState('');

  // While the FuzzyList is opened, the source list is stable so we can use the Array indices as React keys
  const itemsAndKeys = items.map((item, key) => ({item, key}));

  const results = search
    ? fuzzysort.go(search, itemsAndKeys, {key: 'item.title', threshold: -1000, limit: 10}).map((r) => r.obj)
    : itemsAndKeys;

  const dropdownItems = results.map(({item, key}) => (
    <SimpleDropdownItem key={key} onSelect={() => onItemSelect(item)}>
      {item.title}
    </SimpleDropdownItem>
  ));

  const searchBox = (
    <StyledTextBoxDiv>
      <TextBox shouldAutoFocus value={search} onChange={setSearch} />
    </StyledTextBoxDiv>
  );

  return (
    <Dialog>
      <DialogHeader title={searchBox} onRequestClose={() => onItemSelect(null)} />
      <StyledScrollDiv>
        <DropdownList>{dropdownItems}</DropdownList>
      </StyledScrollDiv>
    </Dialog>
  );
};

/*
 * Hook.
 */

interface ListItem {
  title: string;
}

interface FuzzyListDefinition extends SideFrameDialogDefinition {
  items: ReadonlyArray<ListItem>;
}

export function useSideFrameFuzzyList() {
  const [fuzzyListData, setFuzzyListData] = useState<FuzzyListDefinition | null>(null);
  if (!fuzzyListData) {
    return {
      setFuzzyListData,
      fuzzyList: null,
    };
  }

  const onItemSelect = (selection: ListItem | null) => {
    if (!fuzzyListData) {
      return;
    }

    fuzzyListData.onDone(selection);
    setFuzzyListData(null);
  };

  return {
    setFuzzyListData,
    fuzzyList: <SideFrameFuzzyList fuzzyListData={fuzzyListData} onItemSelect={onItemSelect} />,
  };
}
